import type {
  INotificationConfigRepository,
  NotificationConfig,
  NotificationType,
} from '@yawi/domain';
import { getApiClient } from '../../http/apiClient';

type ApiNotificationConfig = Omit<NotificationConfig, 'type'> & {
  notificationType: NotificationType;
  type?: NotificationType;
};

function normalize(raw: ApiNotificationConfig): NotificationConfig {
  const { notificationType, type, ...rest } = raw;
  return { ...rest, type: type ?? notificationType } as NotificationConfig;
}

export class ApiNotificationConfigRepository implements INotificationConfigRepository {
  private get client() { return getApiClient(); }

  async list(): Promise<NotificationConfig[]> {
    const raw = await this.client.get<ApiNotificationConfig[]>('v1/notifications/config');
    return raw.map(normalize);
  }

  async getByType(type: NotificationType): Promise<NotificationConfig | null> {
    const raw = await this.client.get<ApiNotificationConfig | null>(`v1/notifications/config/${type}`);
    return raw ? normalize(raw) : null;
  }

  async update(
    type: NotificationType,
    patch: Partial<Omit<NotificationConfig, 'type' | 'updatedAt'>>
  ): Promise<NotificationConfig> {
    const raw = await this.client.patch<ApiNotificationConfig>(`v1/notifications/config/${type}`, patch);
    return normalize(raw);
  }

  async bulkUpdate(
    updates: Array<{ type: NotificationType } & Partial<Omit<NotificationConfig, 'updatedAt'>>>
  ): Promise<NotificationConfig[]> {
    const raw = await this.client.patch<ApiNotificationConfig[]>('v1/notifications/config', { updates });
    return raw.map(normalize);
  }

  async resetToDefaults(): Promise<NotificationConfig[]> {
    const raw = await this.client.post<ApiNotificationConfig[]>('v1/notifications/config/reset');
    return (raw ?? []).map(normalize);
  }
}
